import OrderSettings from "../../containers/orders/OrderSettings";
import { getUUID } from "../../services/util";

export type OrderStatus = "Accepted" | "Rejected" | "Closed";

export const ORDER_NEW_STATUS = "New" as OrderStatus;

export const moneyFieldWidth = 170;

export interface Order {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  address?: string;
  comments?: string;
  notes?: string;
  status?: OrderStatus;
  created: Date;
  modified?: Date;
  source: string;
  catalogId?: string;
  storeId?: number;
  total: number;
  appFee?: number;
  paid?: boolean;
  isTest?: boolean;
  currency: string;
  orderNum: string;
  items: OrderItem[];

  // Calculated properties
  number: string;
  organization: string;
  paymentId: string;
  salesTax: number;
  salesTaxRate?: number;
  deliveryFee: number;
  discount: number;
  referral?: string;
  effectiveOrderTotal: number;
  orderTotal: number;
  subTotal: number;

  // UI state
  notificationSent: boolean;
  inStock: boolean;

  advanced?: {
    fields: Record<string, string>;
    auditTrail?: AuditEntry[];
  };
}

export interface OrderItem {
  id: number;
  orderId: string;
  itemId: string;
  variantId?: number;
  quantity: number;
  price?: number;
  discount?: number;
  name: string;
  code: string;
  variant?: string;
  stock: number;
  fields: Record<string, string>;
  /** Set for bundle child OrderItems — references the parent's product ItemId (a Guid). */
  parentOrderItemId?: string;
}

export interface OrderSettings {
  price: boolean;
  variants: boolean;
  legacyFields?: string;
  localizedFooter?: string;
  localizedHeader?: string;
  caption?: string;
  localizedCaption?: string;
  advanced?: OrderAdvancedSettings;
}

export interface OrderAdvancedSettings {
  header?: string;
  footer?: string;
  hideStandard: boolean;
  /** Require the built-in standard fields on the order form. Standard fields have no per-field
   * required toggle otherwise — they are shown/hidden as a group via hideStandard. */
  requireOrganization?: boolean;
  requireAddress?: boolean;
  requireComments?: boolean;
  /** Phone sits outside the hideStandard group, so this applies even when standard fields are hidden. */
  requirePhone?: boolean;
  fields: OrderField[];
  itemFields: ProductField[];
  sendNotification: boolean;
  notificationEmail?: string;
  notificationFrom?: string;
  notificationSubject?: string;
  notificationBody?: string;
  manageInventory: boolean;
  payOnly: boolean;
  salesTax: number;
  deliveryFee: number;
  sendPaidNotification: boolean;
  paidNotificationEmail?: string;
  paidNotificationFrom?: string;
  paidNotificationSubject?: string;
  paidNotificationBody?: string;
  optionsTitle?: string;
  addOptionsLabel?: string;
}

export interface OrderField {
  label?: string;
  required: boolean;
  type?: string;
  text?: string;
  columns?: number;
  localizedLabel?: string;
}

export interface ProductField {
  name: string;
  inForm: boolean;
}

// export interface OrderListParams {
//   catalogId?: string;
//   status?: string;
//   search?: string;
//   page?: number;
//   pageSize?: number;
//   sortBy?: string;
//   sortDirection?: "asc" | "desc";
// }

export interface OrderListResponse {
  version: number;
  orders: OrderRef[];
  config: OrderSettingsResponse;
  closedOrdersLoaded?: boolean;
}

export interface OrderRef {
  order: Order;
  count: number;
  total: number;
  //products: string[];
}

// V1 Integration Types (from existing C# classes)
export interface StripeConnect {
  enabled: boolean;
  currency: string;
  appFee?: number;
  created?: Date;
  connected: boolean;
  effectiveAppFee: number;
  //active: boolean;
}

export interface PaymentState {
  //stripeConnectClientId: string;
  stripeConnect?: StripeConnect;
  isShopifyMerchant: boolean;
  isPaidPlan: boolean;
}

export interface OrderSettingsResponse {
  settings: OrderSettings;
  paymentState: PaymentState;
}

export interface PaymentIntent {
  clientSecret: string;
  stripeUserId: string;
  stripeKey: string;
}

// export interface OrderState {
//   canPay: boolean;
//   error?: string;
//   clientSecret?: string;
//   validInventory: boolean;
//   order: OrderModel;
//   fields: string[];
//   disableRequests: boolean;
//   signature: string;
// }

export interface OrderModel {
  currency: string;
  salesTax: number;
  deliveryFee: number;
  orderTotal: number;
  subTotal: number;
  items: OrderProductModel[];
}

export interface OrderProductModel {
  id: string;
  itemId: string;
  variantId?: number;
  code: string;
  name: string;
  quantity: number;
  price?: number;
  stock: number;
  fields: Record<string, string>;
  variants?: OrderVariantModel[];
}

export interface OrderVariantModel {
  variantId: number;
  name: string;
}

//export const DEFAULT_PAGE_SIZE = 50;

export const formatOrderStatus = (status: OrderStatus): string => {
  switch (status?.toLowerCase()) {
    case "accepted":
      return "Accepted";
    case "rejected":
      return "Rejected";
    case "closed":
      return "Closed";
    default:
      return ORDER_NEW_STATUS;
  }
};

export const getOrderStatusColor = (
  status: string
):
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "warning"
  | "info"
  | "success" => {
  switch (status?.toLowerCase()) {
    case "accepted":
      return "success";
    case "rejected":
      return "error";
    case "closed":
      return "default";
    default:
      return "info";
  }
};

/**
 * The ISO 4217 codes this browser's Intl can actually format, which is exactly
 * the set the payment-currency setting is allowed to hold. `supportedValuesOf`
 * is the authoritative list; the fallback covers engines that predate it so the
 * picker is never empty.
 */
export const isoCurrencyCodes = (): string[] => {
  try {
    // Not in our TS lib target yet; available in every browser we support.
    const supportedValuesOf = (
      Intl as unknown as {
        supportedValuesOf?: (key: string) => string[];
      }
    ).supportedValuesOf;
    const codes = supportedValuesOf?.("currency");
    if (codes?.length) return [...codes];
  } catch {
    // fall through to the static list
  }
  return [
    "AUD", "BRL", "CAD", "CHF", "CNY", "CZK", "DKK", "EUR", "GBP", "HKD",
    "ILS", "INR", "JPY", "MXN", "NOK", "NZD", "PLN", "SEK", "SGD", "USD",
    "ZAR",
  ];
};

/**
 * CM2-237: the order payment currency is a user-entered setting, so order rows
 * carry whatever was typed there ("Euro" instead of "EUR"). Intl.NumberFormat
 * throws a RangeError on anything that is not a valid ISO 4217 code, which took
 * the whole order page down through the error boundary. Format when the code is
 * valid, fall back to "<amount> <raw code>" when it is not — a readable total
 * beats a blank screen. This is the only currency formatter orders should use.
 */
export const formatOrderTotal = (
  total: number | null | undefined,
  currency = "USD"
): string => {
  const amount = total ?? 0;
  const code = currency || "USD";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
    }).format(amount);
  } catch {
    return `${amount} ${code}`.trim();
  }
};

/**
 * Sum of a list of orders, one figure per currency. Order-request orders carry
 * no currency (only Stripe payments stamp one), so those sum to a plain number,
 * matching how their row cells read. The footer used to hardcode "$", which put
 * a dollar sign on a Spanish store's euro sales (account 317193, 2026-10-28).
 */
export const formatOrdersTotal = (
  orders: { total?: number | null; currency?: string | null }[]
): string => {
  const sums = new Map<string, number>();
  for (const o of orders) {
    const code = o.currency?.trim() ?? "";
    sums.set(code, (sums.get(code) ?? 0) + (o.total ?? 0));
  }
  const parts = [...sums]
    .filter(([, sum]) => sum !== 0 || sums.size === 1)
    .map(([code, sum]) =>
      code
        ? formatOrderTotal(sum, code)
        : new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(sum)
    );
  return parts.length ? parts.join(" + ") : "0.00";
};

export const formatOrderDate = (date: Date | string): string => {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
};

export const formatOrderNum = (order: Order): string => {
  return order ? order.orderNum || order.id.substring(0, 8) : "";
};

// New order management request types
export interface AuditEntry {
  timestamp: string;
  userEmail: string;
  description: string;
}

export const createOrder = (order: Partial<Order>): Order => {
  const id = order.id || getUUID();
  return {
    id,
    name: order.name || `Order ${new Date().toISOString().split("T")[0]}`,
    created: new Date(),
    modified: new Date(),
    source: "Admin",
    total: 0,
    appFee: 0,
    paid: false,
    currency: "USD",
    orderNum: "",
    items: [],

    number: "",
    organization: "",
    paymentId: "",
    salesTax: 0,
    salesTaxRate: 0,
    deliveryFee: 0,
    discount: 0,
    effectiveOrderTotal: 0,
    orderTotal: 0,
    subTotal: 0,

    // UI state
    notificationSent: false,
    inStock: true,

    advanced: {
      fields: {},
      auditTrail: [],
    },
    ...order,
  };
};

export const getOrderItemTotal = (item: OrderItem) => {
  const price = item.price || 0;
  const quantity = item.quantity || 0;
  const discount = item.discount || 0;
  return price * quantity - discount;
};

export const getOrderSubtotal = (order: Order) => {
  if (!order?.items) return 0;
  // S06: bundle children carry list prices for analytics granularity; the parent
  // line carries the discounted bundle price that the customer actually paid.
  // Excluding children here keeps the subtotal in lock-step with Stripe charges
  // (server-side Order.SubTotal / OrderService apply the same filter).
  return order.items
    .filter((item) => !item.parentOrderItemId)
    .reduce((sum, item) => sum + getOrderItemTotal(item), 0);
};

export const getOrderSubtotalBeforeTaxes = (order: Order) => {
  const subtotal = getOrderSubtotal(order);
  const discount = order?.discount || 0;
  const deliveryFee = order?.deliveryFee || 0;
  return subtotal - discount + deliveryFee;
};

export const getOrderTotal = (order: Order) => {
  const subtotal = getOrderSubtotalBeforeTaxes(order);
  const salesTax = order?.salesTax || 0;
  return subtotal + salesTax;
};
